<script lang="ts">
let activeModals = 0
</script>
<script setup lang="ts">
import { onMounted, onUnmounted, watch, ref, nextTick, useId } from 'vue'
import { X } from '@lucide/vue'

const titleId = useId()

interface Props {
  show: boolean
  title?: string
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl'
  noPadding?: boolean
  closeOnOverlay?: boolean
  persistent?: boolean
  testid?: string
  onBeforeClose?: () => boolean | Promise<boolean>
}

const props = withDefaults(defineProps<Props>(), {
  size: 'md',
  title: '',
  noPadding: false,
  closeOnOverlay: true,
  persistent: false,
  testid: undefined,
  onBeforeClose: undefined
})

const emit = defineEmits<{
  (e: 'close'): void
}>()

async function attemptClose(): Promise<void> {
  if (props.onBeforeClose) {
    const canClose = await props.onBeforeClose()
    if (!canClose) return
  }
  emit('close')
}

const sizeClasses = {
  sm: 'max-w-sm',
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-xl',
  '2xl': 'max-w-2xl'
}

const modalRef = ref<HTMLElement | null>(null)
const isCurrentlyActive = ref(false)
const previousActiveElement = ref<HTMLElement | null>(null)

function handleEsc(e: KeyboardEvent) {
  if (e.key === 'Escape' && props.show && !props.persistent) {
    attemptClose()
  }
}

function handleTab(e: KeyboardEvent) {
  if (!props.show || e.key !== 'Tab' || !modalRef.value) return

  const focusableElements = modalRef.value.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  )
  if (focusableElements.length === 0) return

  const firstElement = focusableElements[0] as HTMLElement
  const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement

  if (e.shiftKey) {
    if (document.activeElement === firstElement) {
      lastElement.focus()
      e.preventDefault()
    }
  } else {
    if (document.activeElement === lastElement) {
      firstElement.focus()
      e.preventDefault()
    }
  }
}

function lockScroll() {
  if (isCurrentlyActive.value) return
  isCurrentlyActive.value = true
  if (activeModals === 0) {
    document.body.style.overflow = 'hidden'
  }
  activeModals++
}

function unlockScroll() {
  if (!isCurrentlyActive.value) return
  isCurrentlyActive.value = false
  if (activeModals > 0) {
    activeModals--
  }
  if (activeModals === 0) {
    document.body.style.overflow = ''
  }
}

watch(() => props.show, async (newVal) => {
  if (newVal) {
    lockScroll()
    previousActiveElement.value = document.activeElement as HTMLElement
    await nextTick()
    if (modalRef.value) {
      const focusableElements = modalRef.value.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
      if (focusableElements.length > 0) {
        (focusableElements[0] as HTMLElement).focus()
      } else {
        modalRef.value.focus()
      }
    }
  } else {
    unlockScroll()
    if (previousActiveElement.value) {
      previousActiveElement.value.focus()
      previousActiveElement.value = null
    }
  }
}, { immediate: true })

onMounted(() => {
  window.addEventListener('keydown', handleEsc)
  window.addEventListener('keydown', handleTab)
})

onUnmounted(() => {
  window.removeEventListener('keydown', handleEsc)
  window.removeEventListener('keydown', handleTab)
  unlockScroll()
})
</script>

<template>
  <Teleport to="body">
    <Transition
      enter-active-class="transition duration-200 ease-out"
      enter-from-class="opacity-0"
      enter-to-class="opacity-100"
      leave-active-class="transition duration-200 ease-in"
      leave-from-class="opacity-100"
      leave-to-class="opacity-0"
    >
      <div
        v-if="show"
        class="fixed inset-0 z-modal-backdrop flex items-center sm:items-center justify-center sm:p-4 bg-text-dark/40 backdrop-blur-sm"
        @click.self="closeOnOverlay && attemptClose()"
      >
        <Transition
          enter-active-class="transition duration-200 ease-lux"
          enter-from-class="opacity-0 translate-y-full sm:translate-y-4 sm:scale-95"
          enter-to-class="opacity-100 translate-y-0 sm:translate-y-0 sm:scale-100"
          leave-active-class="transition duration-200 ease-lux"
          leave-from-class="opacity-100 translate-y-0 sm:translate-y-0 sm:scale-100"
          leave-to-class="opacity-0 translate-y-full sm:translate-y-4 sm:scale-95"
        >
          <div
            v-if="show"
            ref="modalRef"
            role="dialog"
            aria-modal="true"
            tabindex="-1"
            :aria-labelledby="title ? titleId : undefined"
            :data-testid="testid"
            class="bg-surface rounded-t-modal sm:rounded-modal shadow-2xl w-full flex flex-col max-h-[95vh] sm:max-h-[90vh] overflow-hidden border border-border-standard outline-none mt-auto sm:mt-0 z-modal"
            :class="[sizeClasses[size]]"
          >
            <!-- Mobile Handle -->
            <div class="sm:hidden flex justify-center py-3 shrink-0">
              <div class="w-10 h-1 bg-border-standard rounded-full opacity-50" />
            </div>
            <!-- Header -->
            <div
              v-if="title || $slots.header"
              class="flex items-center justify-between p-6 border-b border-border-standard/50 sticky top-0 bg-surface z-10"
            >
              <slot name="header">
                <h3
                  :id="titleId"
                  class="heading-card leading-tight"
                >
                  {{ title }}
                </h3>
              </slot>
              <button 
                v-if="!persistent"
                type="button" 
                data-testid="modal-close-btn"
                aria-label="Tutup dialog"
                class="p-2 rounded-lg text-text-light hover:text-text-dark hover:bg-bg-page transition-colors"
                @click="attemptClose()"
              >
                <X class="size-5" />
              </button>
            </div>

            <!-- Body -->
            <div
              class="flex-1 overflow-y-auto"
              :class="[noPadding ? '' : 'p-6']"
            >
              <slot />
            </div>

            <!-- Footer -->
            <div
              v-if="$slots.footer"
              class="p-6 border-t border-border-standard/50 bg-bg-page/30"
            >
              <slot name="footer" />
            </div>
          </div>
        </Transition>
      </div>
    </Transition>
  </Teleport>
</template>
